export const buildTreeData = (nodes) => {
  const roots = nodes.filter((n) => n.parentId === null);

  const result = [];

  const traverse = (node, path = []) => {
    const fullPath = [...path, node];
    result.push({ ...node, _path: fullPath });
    const children = nodes.filter((n) => n.parentId === node.energyTreeNodeId);
    children.forEach((child) => traverse(child, fullPath));
  };

  roots.forEach((root) => traverse(root));
  return result;
};

// =========================
// Type + node helpers
// =========================
const INFRA = new Set(['Hovedmåler', 'Tavle', 'Undertavle']); // Infrastruktur-typer
const isInfraType = (t) => INFRA.has(t);
const isMainMeter = (t) => t === 'Hovedmåler';
const isDistributionBoard = (t) => t === 'Tavle';
const isSubBoard = (t) => t === 'Undertavle';
const isSEUType = (t) => !!t && !INFRA.has(t);

const isDataNode = (n) =>
  !!n && Array.isArray(n.dataStreams) && n.dataStreams.length > 0;
const isVirtualNode = (n) => !!n && !isDataNode(n);
export const isResidualNode = (n) =>
  !!n &&
  !isDataNode(n) &&
  n.options?.specialNodeType !== null &&
  n.options?.specialNodeType !== 0;

const toMapById = (tree) => new Map(tree?.map((n) => [n.energyTreeNodeId, n]));

const getAncestors = (nodeId, tree) => {
  const byId = toMapById(tree);
  const res = [];
  let cur = byId.get(nodeId);
  while (cur && cur.parentId != null) {
    const p = byId.get(cur.parentId);
    if (!p) break;
    res.push(p);
    cur = p;
  }
  return res;
};

const hasChild = (tree, parentId, predicate) =>
  tree.some(
    (n) => n.parentId === parentId && (predicate ? predicate(n) : true),
  );

//const findRoot = (tree) => tree.find((n) => n.parentId == null);

// Helper return shapes that accept optional UI flags
const ok = (validationMessage = 'OK', extras = {}) => ({
  valid: true,
  validationMessage,
  ...extras,
});
const fail = (validationMessage, extras = {}) => ({
  valid: false,
  validationMessage,
  ...extras,
});

// =========================
// VALIDATE: Add Node
// =========================
/**
 * @param {Object} params
 * @param {string} params.targetType
 * @param {Object|null} params.parent
 * @param {Array<Object>} params.tree
 * @returns {{valid:boolean, validationMessage:string}}
 */
export const validateAddNode = ({ targetType, parent, tree }) => {
  //Hovedmåler må ikke kunne tilføjes under en SEU, Tavle eller Undertavle

  // SEU med data som parent: forbudt at tilføje under
  if (parent && isSEUType(parent.type) && isDataNode(parent)) {
    return fail('Kan ikke tilføje node under en SEU node med data.');
  }

  // Residual som parent: forbudt at tilføje under
  if (parent && isResidualNode(parent)) {
    return fail('Kan ikke tilføje node under en residual node.');
  }

  // Hovedmåler-regler
  if (isMainMeter(targetType)) {
    const mainMeterExists = tree.some((n) => isMainMeter(n.type));
    const parentIsMainMeter = parent && isMainMeter(parent.type);
    const parentHasData = parent && isDataNode(parent);
    if (mainMeterExists && parentIsMainMeter && isVirtualNode(parent)) {
      return ok(
        'Gyldig: Hovedmåler som child af virtuel Hovedmåler er tilladt.',
      );
    }

    if (mainMeterExists && parentIsMainMeter && parentHasData) {
      return fail('Hovedmåler eksisterer allerede som en data node.');
    }

    // if (parent && parent.type !== null) {
    //   return fail('Hovedmåler skal være roden (ingen parent).');
    // }
    return ok('Gyldig konfiguration.');
  }

  // Tavle kun under Hovedmåler eller node uden data (virtuel)
  if (isDistributionBoard(targetType)) {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && !isDataNode(parent);
    if (!parentIsMain && !parentIsVirtual) {
      return fail(
        'Tavle må kun placeres under Hovedmåler eller node uden data.',
      );
    }
    return ok();
  }

  // Undertavle kun under (eller i en gren der indeholder) en Tavle.
  // Og må ikke placeres under en gren som allerede indeholder en Undertavle.
  if (isSubBoard(targetType)) {
    const ancestors = parent?.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];
    const hasTavleInLineage =
      parent?.type === 'Tavle' || ancestors.some((a) => a.type === 'Tavle');
    const hasUndertavleInLineage =
      parent?.type === 'Undertavle' ||
      ancestors.some((a) => a.type === 'Undertavle');

    if (hasUndertavleInLineage) {
      return fail(
        'En Undertavle må ikke placeres under en gren som allerede indeholder en Undertavle.',
      );
    }
    if (!hasTavleInLineage) {
      return fail(
        'Undertavle må kun placeres under (eller under en gren med) en Tavle.',
      );
    }
    return ok();
  }

  // Default: tillad
  return ok();
};

// =========================
// VALIDATE: Edit Node (change type)
// =========================
/**
 * @param {Object} params
 * @param {Object} params.currentNode
 * @param {string} params.targetType
 * @param {Array<Object>} params.tree
 * @param {Array<Object>} [params.dataStreams] - når UI sender datakilder separat
 * @returns {{
 *   valid:boolean,
 *   validationMessage:string,
 *   isUnitSelectionDisabled?:boolean,
 *   unitSelectionDisabledReason?:string
 * }}
 */

export const validateEditNode = ({
  currentNode,
  targetType,
  tree,
  dataStreams,
}) => {
  if (!currentNode) return fail('Mangler den nuværende node.');

  const byId = toMapById(tree);
  const parent =
    currentNode.parentId != null ? byId.get(currentNode.parentId) : null;

  // Brug eksplicit UI dataStreams hvis givet
  const currentHasData = Array.isArray(dataStreams)
    ? dataStreams.length > 0
    : isDataNode(currentNode);

  // Precomputed helpers
  const id = currentNode.energyTreeNodeId;
  const hasAnyChildren =
    typeof currentNode?._directSubordinates === 'number'
      ? currentNode._directSubordinates > 0
      : hasChild(tree, id);
  const hasUndertavleChildren = hasChild(
    tree,
    id,
    (n) => n.type === 'Undertavle',
  );

  // =========================
  // #1 Hovedmåler-regler
  // =========================
  if (isMainMeter(currentNode.type)) {
    if (!isMainMeter(targetType)) {
      return fail(
        '#1 Hovedmåler kan ikke ændre type. Den skal forblive Hovedmåler.',
      );
    }
    return ok(
      '#1 Dette er en virtuel Hovedmåler - det er muligt at rette i datakilder.',
      {
        isUnitSelectionDisabled: false,
        unitSelectionDisabledReason: 'Virtuel Hovedmåler kan ikke ændres.',
        isTypeSelectionDisabled: true,
        typeSelectionDisabledReason: 'Virtuel Hovedmåler kan ikke ændres.',
      },
    );
  }

  // =========================
  // #2 SEU med data -> må kun forblive SEU-type
  // =========================
  if (isSEUType(currentNode.type) && currentHasData) {
    if (isInfraType(targetType)) {
      return fail(
        '#2 En SEU med tilknyttede datakilder kan kun ændres til en anden SEU-type.',
      );
    }
    return ok(
      '#2 En SEU med tilknyttede datakilder kan kun ændres til en anden SEU-type.',
    );
  }

  // =========================
  // #4 Tavle-regler (samlet)
  // =========================
  if (isDistributionBoard(currentNode.type)) {
    // #4a Undertavle-børn -> låst til Tavle
    if (hasUndertavleChildren) {
      if (!isDistributionBoard(targetType)) {
        return fail(
          '#4 En Tavle med undertavle-børn kan kun ændres til en anden Tavle, da Undertavle kun må placeres under en Tavle.',
        );
      }
      return ok(
        '#4 En Tavle med børn af typen Undertavle skal forblive en Tavle.',
      );
    }

    // #4b Tavle -> SEU kun hvis: ingen børn + ingen data
    if (isSEUType(targetType)) {
      if (hasAnyChildren) {
        return fail(
          '#4 Tavle med børn kan ikke ændres til en SEU. Fjern eller omplacer børnene først.',
        );
      }
      if (currentHasData) {
        // (dækker også den gamle #7)
        return fail(
          '#4/#7 Tavle med data kan kun ændres til en anden Tavle (ikke SEU). Ryd data først, hvis du vil ændre til en SEU.',
        );
      }
      return ok('#4 Tavle uden børn og uden data kan ændres til en SEU-type.', {
        isUnitSelectionDisabled: true,
        unitSelectionDisabledReason:
          '#4 Ved ændring fra Tavle til SEU er enhedsvalg låst.',
      });
    }

    // #10 Tavle kan ikke blive Hovedmåler eller Undertavle
    if (isMainMeter(targetType) || isSubBoard(targetType)) {
      return fail(
        '#10 En Tavle kan ikke ændres til Hovedmåler eller Undertavle.',
      );
    }

    // Forbliv Tavle eller skift->Tavle
    return ok('#4 Det er kun den nuværende type Tavle der er tilladt.');
  }

  // =========================
  // #5 Undertavle-regler (samlet)
  // =========================
  if (isSubBoard(currentNode.type)) {
    // #5a Undertavle med data -> må kun forblive Undertavle
    if (currentHasData) {
      // må gerne ændres til SEU hvis ingen børn
      if (!hasAnyChildren && isSEUType(targetType)) {
        return ok(
          '#5 Undertavle med data og uden børn kan ændres til en anden Undertavle eller SEU.',
          {
            isUnitSelectionDisabled: true,
            unitSelectionDisabledReason:
              '#5 Ved ændring fra Undertavle til SEU er enhedsvalg låst.',
            isTypeSelectionDisabled: false,
            typeSelectionDisabledReason: '',
          },
        );
      }

      if (!isSubBoard(targetType)) {
        return fail(
          '#5 Undertavle med data kan kun ændres til en anden Undertavle (ikke Hovedmåler eller Tavle).',
        );
      }

      return ok(
        '#5 Undertavle med data og børn kan kun ændres til en anden Undertavle. Fjern datakilder først, hvis du vil ændre til en virtuel SEU. Fjern alle børn hvis du vil ændre til en data SEU',
      );
    }

    // #5b Undertavle uden data -> kan ændres til SEU (låser enhedsvalg)
    if (isSEUType(targetType)) {
      return ok(
        `#5 Virtuel ${currentNode.type} kan ændres til virtuel ${targetType}.`,
        {
          isUnitSelectionDisabled: true,
          unitSelectionDisabledReason:
            '#5 Ved ændring fra Undertavle til SEU er enhedsvalg låst.',
        },
      );
    }

    // #5c Undertavle kan ikke blive Hovedmåler eller Tavle
    if (isMainMeter(targetType) || isDistributionBoard(targetType)) {
      return fail('#5 Undertavle kan ikke ændres til Hovedmåler eller Tavle.');
    }

    // #8/#14 dækkes af #5b (Undertavle -> SEU uden data)
    return ok(
      `#5 Ændring fra virtuel ${currentNode.type} kan ændres til en anden virtuel SEU. Tilføjelse af datakilder er kun muligt på nuværende Undertavle.`,
    );
  }

  // =========================
  // #6 Virtuel node med SEU-børn med data -> lås enhedsvalg
  // =========================
  if (isVirtualNode(currentNode)) {
    const ancestors = parent?.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];
    const hasTavleInLineage =
      parent?.type === 'Tavle' || ancestors.some((a) => a.type === 'Tavle');

    const childSEUWithData = tree
      .filter((n) => n.parentId === id)
      .some((c) => isSEUType(c.type) && isDataNode(c));

    if (childSEUWithData && isSubBoard(targetType) && hasTavleInLineage) {
      return ok(
        '#6  Virtuel SEU med børn kan ændres til en anden virtuel SEU eller Undertavle med Datakilder.',
        {
          isUnitSelectionDisabled: false,
        },
      );
    }

    if (
      childSEUWithData &&
      isDistributionBoard(targetType) &&
      !hasTavleInLineage
    ) {
      return ok(
        '#6 Virtuel SEU med børn kan ændres til en anden virtuel SEU eller Tavle med Datakilder.',
        {
          isUnitSelectionDisabled: false,
        },
      );
    }

    // if (childSEUWithData) {
    //   return ok('#6 men datakilder kan ikke ændres pga. børn med data.', {
    //     isUnitSelectionDisabled: true,
    //     unitSelectionDisabledReason:
    //       '#6 Node har børn med data, så datakilder kan ikke ændres.',
    //   });
    // }
  }

  // =========================
  // #9 Virtuel (ikke-infra) må ikke blive infrastruktur
  // =========================
  if (
    isVirtualNode(currentNode) &&
    !isInfraType(currentNode.type) &&
    isInfraType(targetType)
  ) {
    return fail(
      '#9 Virtuel node (uden data) kan kun ændres til en SEU-type (ikke infrastruktur).',
    );
  }

  // =========================
  // Måltype-specifikke regler (uanset nuværende type)
  // =========================

  // #11 Target = Hovedmåler -> unik + root
  if (isMainMeter(targetType)) {
    const otherMainExists = tree.some(
      (n) =>
        isMainMeter(n.type) &&
        n.energyTreeNodeId !== currentNode.energyTreeNodeId,
    );
    if (otherMainExists) return fail('#11 Hovedmåler eksisterer allerede.');
    if (currentNode.parentId !== null)
      return fail('#11 Hovedmåler skal være roden (ingen parent).');
    return ok('#11');
  }

  // #12 Target = Tavle -> parent skal være Hovedmåler eller virtuel
  if (isDistributionBoard(targetType)) {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && !isDataNode(parent);
    if (!parentIsMain && !parentIsVirtual) {
      return fail(
        '#12 Tavle må kun placeres under Hovedmåler eller node uden data.',
      );
    }
    return ok('#12');
  }

  // #13 Target = Undertavle -> i linje med (eller under en gren med) Tavle
  if (isSubBoard(targetType)) {
    const ancestors = parent?.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];
    const hasTavleInLineage =
      parent?.type === 'Tavle' || ancestors.some((a) => a.type === 'Tavle');
    const hasUndertavleInLineage =
      parent?.type === 'Undertavle' ||
      ancestors.some((a) => a.type === 'Undertavle');

    if (!hasTavleInLineage && !hasUndertavleInLineage) {
      return fail(
        '#13 Undertavle må kun placeres under (eller under en gren med) en Tavle.',
      );
    }
    return ok('#13');
  }

  // =========================
  // Default
  // =========================
  return ok('Denne konfiguration er tilladt.');
};

export const filterOptionGroupsForAdd = (parent, tree, groupedOptions) => {
  return Object.fromEntries(
    Object.entries(groupedOptions).map(([groupLabel, options]) => {
      const filtered = options.filter(({ value }) => {
        const { valid } = validateAddNode({
          targetType: value,
          parent,
          tree,
        });
        return valid;
      });
      return [groupLabel, filtered];
    }),
  );
};

export const filterOptionGroupsForEdit = (
  parent,
  tree,
  groupedOptions,
  dataStreams,
) => {
  return Object.fromEntries(
    Object.entries(groupedOptions).map(([groupLabel, options]) => {
      const filtered = options.filter(({ value }) => {
        const { valid } = validateEditNode({
          targetType: value,
          parent,
          currentNode: parent,
          tree,
          dataStreams: dataStreams,
        });
        return valid;
      });
      return [groupLabel, filtered];
    }),
  );
};

export const actionValidation = ({
  action,
  targetType,
  parent,
  currentNode,
  dataStreams,
  tree,
  options,
  isMainMeterUsed,
}) => {
  switch (action) {
    case 'add':
      return validateAddNode({ targetType, parent, tree, options });
    case 'edit':
      return validateEditNode({ currentNode, targetType, tree, dataStreams });
    case 'move':
      return validateMoveNode({
        action,
        targetType,
        parentType: parent.type,
        currentNode: currentNode,
        parent: parent,
        parentUnits: parent.dataStreams?.length || 0,
        isMainMeterUsed: isMainMeterUsed,
      });
    default:
      return { isValid: false, invalidReason: 'Unknown action' };
  }
};

export const validateMoveNode = ({
  targetType,
  parentType,
  parent,
  currentNode,
  isMainMeterUsed,
  tree,
}) => {
  if (isMainMeter(targetType) && isMainMeterUsed) {
    return { isValid: false, invalidReason: 'Hovedmåler already exists' };
  }

  if (isResidualNode(parent)) {
    return {
      isValid: false,
      invalidReason:
        'Det er ikke muligt at flytte en node under en residual node.',
    };
  }

  if (
    parent &&
    isInfraType(targetType) &&
    isDataNode(parent) &&
    isSEUType(parent.type)
  ) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  if (
    parent &&
    isSubBoard(targetType) &&
    !getAncestors(parent.energyTreeNodeId, tree).some(
      (a) => a.type === 'Tavle',
    ) &&
    parent.type !== 'Tavle'
  ) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  // cant move a residual node under a SEU node with data
  if (parent && isSEUType(parentType) && isResidualNode(currentNode)) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${isResidualNode(currentNode) ? 'Residual Node' : ''} under en ${parent.type} node.`,
    };
  }

  // cant move a node under a SEU node with data
  if (parent && isSEUType(parentType) && isDataNode(parent)) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  return { isValid: true };
};

export const getParent = (nodeId, treeData) => {
  const map = new Map(treeData.map((n) => [n.energyTreeNodeId, n]));
  const current = map.get(nodeId);
  return map.get(current?.parentId) || null;
};

export const getChildren = (nodeId, treeData) => {
  return treeData.filter((n) => n.parentId === nodeId);
};

export const isNodeDescendant = (childId, parentId, treeData) => {
  const childNode = treeData.find((node) => node.energyTreeNodeId === childId);
  if (!childNode || !childNode.parentId) return false;
  if (childNode.parentId === parentId) return true;
  return isNodeDescendant(childNode.parentId, parentId, treeData);
};

export const getUnitQueryOptions = (unit) => {
  if (!unit || !unit.UnitType || !unit.UnitType.UnitTypeId) return null;
  const unitTypeId = unit.UnitType.UnitTypeId;
  const switchCase = {
    61: { unitTypeInputId: 6101, dataType: 'power-consumption' },
    28: {
      unitTypeInputId: 2801,
      dataType:
        unit?.UnitSensors[0]?.Sensor?.CalibrationType === 'PowerDisaggregation'
          ? 'power'
          : 'apparent-power',
    },
    30: { unitTypeInputId: 0, dataType: 'power' },
    62: {
      unitTypeInputId: 6201,
      dataType: 'accumulated-heat',
    },
    60: {
      unitTypeInputId: 6001,
      dataType: 'accumulated-flow',
    },
    63: { unitTypeInputId: 6303, dataType: 'heat-consumption' },
  };

  // Return the mapped value if it exists, otherwise return null
  return switchCase[unitTypeId] || null;
};

export const mapUnitsToDataStreams = (units) => {
  return units
    .map((unit) => {
      const queryOptions = getUnitQueryOptions(unit);
      if (!queryOptions) return null;
      return {
        unitId: unit.UnitId,
        unitTypeInputId: queryOptions.unitTypeInputId ?? null, // Safe access
        dataType: queryOptions.dataType || 'power', // Defaulting to "Power"
        aggregateType: 'Hour', // Defaulting to "Hour"
      };
    })
    .filter(Boolean);
};

export const canAddNode = (clickedNode) => {
  if (!clickedNode) return false;
  console.table({
    type: clickedNode.type,
    isInfra: isInfraType(clickedNode.type),
    isSEU: isSEUType(clickedNode.type),
    isDataNode: isDataNode(clickedNode),
    isVirtualNode: isVirtualNode(clickedNode),
    isResidualNode: isResidualNode(clickedNode),
  });
};

// Build a fast lookup
const mapById = (tree) => new Map(tree.map((n) => [n.energyTreeNodeId, n]));

const computeNodeViolations = (node, tree, counts) => {
  const issues = [];
  const parent =
    node.parentId != null ? mapById(tree).get(node.parentId) : null;

  // Rule: only one Hovedmåler in the entire tree, and it must be root (no parent)
  if (node.type === 'Hovedmåler') {
    if (counts.hovedmålerCount > 1) {
      issues.push({
        code: 'MULTIPLE_MAIN_METERS',
        message: 'Der må kun være én Hovedmåler i træet.',
      });
    }
    if (node.parentId !== null) {
      issues.push({
        code: 'MAIN_METER_NOT_ROOT',
        message: 'Hovedmåler skal være roden (ingen parent).',
      });
    }
  }

  // Rule: Tavle only directly under Hovedmåler or a Virtual node
  if (node.type === 'Tavle') {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && isVirtualNode(parent);
    if (!parentIsMain && !parentIsVirtual) {
      issues.push({
        code: 'TAVLE_PARENT_INVALID',
        message: 'Tavle må kun placeres under Hovedmåler eller node uden data.',
      });
    }
  }

  // Rule: Undertavle allowed if ANY ancestor is a Tavle (not just direct parent)
  if (node.type === 'Undertavle') {
    const lineageHasTavle =
      parent?.type === 'Tavle' ||
      getAncestors(node.energyTreeNodeId, tree).some((a) => a.type === 'Tavle');

    if (!lineageHasTavle) {
      issues.push({
        code: 'UNDERTAVLE_WITHOUT_TAVLE_ANCESTOR',
        message: 'Undertavle må kun placeres under (en gren med) en Tavle.',
      });
    }
  }

  return issues;
};

export const validateTree = (tree) => {
  // Count Hovedmåler
  const hovedmålerCount = tree.filter((n) => n.type === 'Hovedmåler').length;

  const counts = { hovedmålerCount };
  const byNode = new Map();

  for (const node of tree) {
    const issues = computeNodeViolations(node, tree, counts);
    if (issues.length) {
      byNode.set(node.energyTreeNodeId, issues);
    }
  }

  // Convenience outputs
  const invalidNodeIds = new Set([...byNode.keys()]);
  const flatErrors = [...byNode.entries()].map(([nodeId, issues]) => ({
    nodeId,
    issues,
  }));

  return {
    hasErrors: invalidNodeIds.size > 0,
    invalidNodeIds,
    violationsByNode: byNode, // Map<nodeId, {code,message}[]>
    errors: flatErrors, // Array<{nodeId, issues:[]}>
  };
};

export const getDublicateViolations = (listOfNodes, allNodes) => {
  if (!listOfNodes || listOfNodes.length === 0) return [];
  if (!allNodes || allNodes.length === 0) return [];
  // Create a Set of selected node IDs for fast lookup
  const selectedNodeIds = new Set(listOfNodes.map((node) => node.id));

  // Get full node data for the selected nodes
  const fullNodes = allNodes.filter((node) =>
    selectedNodeIds.has(node.energyTreeNodeId),
  );

  // Create a map for quick node lookup
  const nodeMap = new Map(
    allNodes.map((node) => [node.energyTreeNodeId, node]),
  );

  const duplicates = [];

  // Helper function to check if any ancestor is selected
  const hasSelectedAncestor = (node) => {
    let currentParentId = node.parentId;

    while (currentParentId !== null) {
      if (selectedNodeIds.has(currentParentId)) {
        return true;
      }
      const parentNode = nodeMap.get(currentParentId);
      if (!parentNode) break;
      currentParentId = parentNode.parentId;
    }

    return false;
  };

  // Check if any node has an ancestor that is also selected
  for (const node of fullNodes) {
    if (hasSelectedAncestor(node)) {
      duplicates.push(node.name || node.energyTreeNodeId);
    }
  }

  return duplicates;
};

export const getRootNode = (tree) => {
  return tree?.find((n) => n.parentId === null) || null;
};
